import httpRequest from '@/utils/request'
import { optionalNewApi } from '@/utils/requestapis'
import { stockSelection } from './stockSelection'

// Use existing account/catalog APIs only. A quote-provider ranking is not proof
// that a stock has been enabled for trading in this installation.
export async function resolveDefaultStock(market, request = httpRequest, cancelled = () => false, preferredContractId = 0) {
  if (!['cn', 'hk', 'us'].includes(market) || cancelled()) return null
  const get = (url, params) => request({ url: 'vexe/v1/' + url, method: 'get', params })
  // Prefetch without letting a lower-priority failure override a valid holding.
  const watchParams = { type: market === 'us' ? 2 : market === 'hk' ? 1 : 0, page: 1, limit: 20 }
  const watchlistRequest = (request === httpRequest ? optionalNewApi(watchParams) : get('user/optionalNew', watchParams))
    .then(value => ({ value }), error => ({ error }))
  let preferredId = 0
  const selected = (item, contractId) => {
    const stock = stockSelection({ ...item, market })
    return stock.symbol && stock.market === market ? { ...stock, contractId: preferredId || contractId } : null
  }
  async function pages(url, params, visit) {
    for (let page = 1; !cancelled(); page++) {
      const result = await get(url, { ...params, page, page_size: 50 })
      if (cancelled()) return null
      if (!result || !Array.isArray(result.data)) throw new Error('股票列表加载失败')
      const match = await visit(result.data)
      if (match || cancelled()) return match
      if (!result.data.length || page >= Number(result.last_page || Math.ceil(Number(result.total || result.data.length) / 50))) return null
    }
    return null
  }
  const holding = async (contracts) => {
    const findHolding = async (contract) => {
      if (market === 'cn') {
        const result = await get('consult/position', { consult_id: contract.id })
        if (!result || !Array.isArray(result.order_list)) throw new Error('持仓加载失败')
        for (const row of result.order_list) {
          if (Number(row.hand) > 0) { const stock = selected(row, contract.id); if (stock) return stock }
        }
      } else {
        const stock = await pages('contract/positionList', { contract_id: contract.id }, rows => {
          for (const row of rows) {
            if (Number(row.total_num || Number(row.rest_num || 0) + Number(row.disable_num || 0)) > 0) {
              const stock = selected(row, contract.id); if (stock) return stock
            }
          }
          return null
        })
        if (stock) return stock
      }
      return null
    }
    // Bound concurrency, while retaining contract order and stopping after a match.
    for (let offset = 0; offset < contracts.length && !cancelled(); offset += 4) {
      const pending = contracts.slice(offset, offset + 4).map(contract =>
        findHolding(contract).then(value => ({ value }), error => ({ error })))
      for (const task of pending) {
        const result = await task
        if (cancelled()) return null
        if (result.error) throw result.error
        if (result.value) return result.value
      }
    }
    return null
  }
  let stock
  if (market === 'cn') {
    const contracts = await get('consult/float_list', { type: 0 })
    if (!Array.isArray(contracts)) throw new Error('合约加载失败')
    const preferred = contracts.find(item => Number(item.id) === Number(preferredContractId))
    if (preferred) preferredId = Number(preferred.id)
    stock = await holding(preferred ? [preferred] : contracts)
  } else stock = await pages('contract/contractList', { status: 0, mkt_type: market === 'us' ? 2 : 1 }, holding)
  if (stock || cancelled()) return stock
  const prefetched = await watchlistRequest
  if (cancelled()) return null
  if (prefetched.error) throw prefetched.error
  const watchlist = prefetched.value
  if (!watchlist || !Array.isArray(watchlist.data)) throw new Error('自选加载失败')
  for (const row of watchlist.data) { stock = selected(row); if (stock) return stock }
  const today = new Date(); today.setHours(0, 0, 0, 0)
  for (const prefix of market === 'us' ? ['us'] : market === 'hk' ? ['hk'] : ['sh', 'sz', 'bj']) {
    stock = await pages('stock/searchStock', { code: prefix }, rows => {
      for (const row of rows) {
        if (Number(row.status) !== 1 || Number(row.suspension) !== 1) continue
        if (Number(row.classify) !== (market === 'us' ? 2 : market === 'hk' ? 1 : 0)) continue
        if (Number(row.suspension_time) > 0 && today.getTime() / 1000 - Number(row.suspension_time) < 86400) continue
        const candidate = selected(row); if (candidate) return candidate
      }
      return null
    })
    if (stock || cancelled()) return stock
  }
  return null
}
